import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { titleize, humanize } from 'underscore.string';
import classnames from 'classnames';

import CargomaticInput from 'components/v2/input';
import Card from 'components/v2/card';
import MultiSelectUser from 'components/site/multi-user-search';
import Alert from 'components/ui/Alert';
import { findUsers } from 'utils/UserWebAPIUtils';
import { updateYard, addYard } from 'utils/YardAPIUtils';
import { showToast } from 'stores/ToastStore';
import ErrorLabel from 'components/v2/forms/ErrorLabel';
import ToggleSwitch from 'components/v2/forms/ToggleSwitch';
import Label from 'components/v2/forms/Label';
import Badge from 'components/v2/Badge';
import { handleValidateYardForm, parseValidationError } from './utils';
import { withYardLayout } from './YardLayout';

function YardDetails({ yard, isNewYard, onUpdate }) {
  const {
    nameInternal,
    nameExternal,
    spots,
    vendor,
    address,
    id,
    contactName,
    contactNumber,
    marketId,
    status,
    secure,
  } = yard;

  const history = useHistory();
  const { t } = useTranslation();

  const initialFormState = {
    nameInternal,
    nameExternal,
    spots,
    address,
    contactName,
    contactNumber: contactNumber ?? '',
    marketId,
    secure: secure ?? false,
  };
  const [yardFormData, setYardFormData] = useState(initialFormState);
  const [vendorInitialState, setVendorInitialState] = useState([]);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [multiSelectItems, setMultiSelectItems] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(true);
  const [isYardStatusActive, setIsYardStatusActive] = useState(
    status === 'active',
  );
  const [isSecure, setIsSecure] = useState(secure ?? false);

  function handleYardFormChange(value) {
    setYardFormData((prevState) => {
      return {
        ...prevState,
        ...value,
      };
    });
  }

  const getError = (fieldName) => {
    const error = new Error(
      t('FIELD_IS_REQUIRED', { field: humanize(fieldName) }),
    );
    return error;
  };

  const yardPayload = {
    data: {
      ...yardFormData,
      contactNumber: yardFormData.contactNumber || null,
      vendor: yardFormData.vendor || multiSelectItems[0]?.id,
      status: isYardStatusActive ? 'active' : 'inactive',
      secure: isSecure,
    },
  };

  const handleUpdateYard = async () => {
    setIsFormValid(false);
    const { errors } = await updateYard({ ...yardPayload, id });
    if (errors) {
      const validationError = parseValidationError(errors);
      showToast(
        t('UPDATE_YARD_ERROR'),
        t('UPDATE_YARD_FAILED_WITH_ERROR', {
          error: validationError || errors[0].message,
        }),
        'error',
      );
      return;
    }
    showToast(
      t('YARD_UPDATED_SUCCESSFULLY'),
      t('YARD_UPDATED_SUCCESSFULLY'),
      'success',
    );
    // update vendor initial state after save
    setVendorInitialState(multiSelectItems);
    setIsReadOnly(true);
    // update wrapper
    onUpdate();
  };

  const handleAddYard = async () => {
    setIsFormValid(false);
    const { data, errors } = await addYard(yardPayload);
    if (errors) {
      const validationError = parseValidationError(errors);
      showToast(
        t('ADD_YARD_ERROR'),
        t('ADD_YARD_FAILED_WITH_ERROR', {
          error: validationError || errors[0].message,
        }),
        'error',
      );
      return;
    }
    showToast(
      t('YARD_ADDED_SUCCESSFULLY'),
      t('YARD_ADDED_SUCCESSFULLY'),
      'success',
    );
    // update vendor initial state after save
    setVendorInitialState(multiSelectItems);
    setIsReadOnly(true);
    history.push(`/yards/${data.addYard?.id}`);
  };

  const handleEdit = () => setIsReadOnly(false);

  const handleSave = () => {
    const isHandleSave = true;
    handleValidateYardForm({
      yardFormData,
      multiSelectItems,
      getError,
      setIsFormValid,
      setErrors,
      isHandleSave,
    });
  };

  const handleCancel = () => {
    // Set initial state
    setYardFormData(initialFormState);
    setMultiSelectItems(vendorInitialState);
    setIsYardStatusActive(status === 'active');
    setIsSecure(secure ?? false);
    if (isNewYard) {
      history.push('/yards');
      return;
    }
    setIsReadOnly(true);
  };

  // onSave handler
  useEffect(() => {
    if (!isFormValid) return;
    if (isNewYard) {
      handleAddYard();
      return;
    }
    handleUpdateYard();
  }, [isFormValid]);

  // Validate each time yard data was changed
  useEffect(() => {
    const isFailedValidation = Object.keys(errors).length;
    const isHandleSave = false;
    if (isFailedValidation) {
      handleValidateYardForm({
        yardFormData,
        multiSelectItems,
        getError,
        setIsFormValid,
        setErrors,
        isHandleSave,
      });
    }
  }, [yardFormData, multiSelectItems.length]);

  useEffect(() => {
    const shouldFetchVendor = vendor && !multiSelectItems.length;
    if (shouldFetchVendor) {
      const fetchVendor = async () => {
        setVendorLoading(true);
        const fetchedVendor = await findUsers({
          _id: vendor,
        });
        setMultiSelectItems(fetchedVendor?.entity?.items);
        setVendorInitialState(fetchedVendor?.entity?.items);
        setVendorLoading(false);
      };
      fetchVendor().catch((e) => {
        console.error('Error fetching vendor:', e.message);
        setVendorLoading(false);
      });
    }
  }, [vendor]);

  const vendorLabelClasses = classnames({
    'form-label block mb-2': true,
    'text-red-500': errors.vendor,
  });

  function renderErrors() {
    if (Object.keys(errors).length === 0) {
      return null;
    }
    return (
      <Alert className="my-4" title={t('YARD_VALIDATION_FAILED')} type="error">
        <ul data-testid="addYardErrors">
          {Object.values(errors).map((error, i) => (
            <li key={i}>{error.message}</li>
          ))}
        </ul>
      </Alert>
    );
  }
  const isStatusActive = status === 'active';
  const statusBadgeColor = isStatusActive ? 'green' : 'grey';
  return (
    <>
      <div
        className="flex items-center mb-4"
        data-testid="yardInformationTitleSection"
      >
        <h3
          className="text-lg font-medium leading-6 text-gray-900"
          data-testid="yardInformationTitle"
        >
          {t('YARD_INFORMATION')}
        </h3>
        {!isNewYard && (
          <Badge className="ml-3 mt-1" color={statusBadgeColor}>
            {isStatusActive ? t('ACTIVE') : t('INACTIVE')}
          </Badge>
        )}
      </div>
      {renderErrors()}
      {vendorLoading ? (
        <p className="lead">{titleize(t('LOADING'))}</p>
      ) : (
        <Card className="bg-white px-4 py-5 sm:p-6">
          <div className="grid grid-cols-6 gap-6">
            <div className="col-span-6 sm:col-span-3">
              <CargomaticInput
                name="yardnameInternal"
                type="text"
                label={t('YARD_NAME_INTERNAL')}
                placeholder={t('YARD_NAME_INTERNAL')}
                error={errors.nameInternal}
                value={yardFormData.nameInternal}
                readOnly={!!isReadOnly && !isNewYard}
                onChange={(value) =>
                  handleYardFormChange({ nameInternal: value })
                }
              />
            </div>

            <div className="col-span-6 sm:col-span-3">
              <CargomaticInput
                name="yardNameExternal"
                type="text"
                label={t('YARD_NAME_CUSTOMER_FACING')}
                placeholder={t('YARD_NAME_CUSTOMER_FACING')}
                error={errors.nameExternal}
                value={yardFormData.nameExternal}
                readOnly={!!isReadOnly && !isNewYard}
                onChange={(value) =>
                  handleYardFormChange({ nameExternal: value })
                }
              />
            </div>

            <div
              className={`col-span-6 sm:col-span-3 ${
                errors.vendor ? 'error' : ''
              }`}
              data-testid="addYardVendorInput"
            >
              <label className={vendorLabelClasses}>
                {t('YARD_HEADER_VENDOR')}
              </label>
              <MultiSelectUser
                placeholder={t('SEARCH_VENDOR')}
                type="vendors"
                onSelect={(_item, value) => {
                  setMultiSelectItems([value]);
                  handleYardFormChange({ vendor: value._id });
                }}
                onRemove={(_item, value) => setMultiSelectItems([])}
                selectedItems={multiSelectItems}
                multiple={false}
                className={classnames(
                  {
                    'pointer-events-none': isReadOnly && !isNewYard,
                  },
                  'customer-search',
                )}
              />
              {errors.vendor && (
                <ErrorLabel id={id} className="mt-1">
                  {errors.vendor.message}
                </ErrorLabel>
              )}
            </div>

            <div className="col-span-6 sm:col-span-2">
              <CargomaticInput
                name="yardSpots"
                type="number"
                label={t('NUMBER_OF_SPOTS')}
                placeholder={t('NUMBER_OF_SPOTS')}
                min={0}
                error={errors.spots}
                value={yardFormData.spots}
                readOnly={!!isReadOnly && !isNewYard}
                onChange={(value) =>
                  handleYardFormChange({ spots: Number(value) })
                }
              />
            </div>

            {(!isReadOnly || isNewYard) && (
              <div className="col-span-6 sm:col-span-3 flex items-center">
                <ToggleSwitch
                  enabled={isYardStatusActive}
                  onChange={setIsYardStatusActive}
                />
                <Label className="ml-2">{t('ACTIVE_QUESTION_MARK')}</Label>
              </div>
            )}

            {(!isReadOnly || isNewYard) && (
              <div className="col-span-6 sm:col-span-2 flex items-center">
                <ToggleSwitch
                  id="secure-checkbox"
                  enabled={isSecure}
                  onChange={setIsSecure}
                />
                <Label htmlFor="secure-checkbox" className="ml-2">
                  {t('SECURE')}
                </Label>
              </div>
            )}

            <div className="col-span-6" data-testid="addYardAddressInput">
              <CargomaticInput
                name="yardContactAddress"
                type="location"
                label={t('YARD_ADDRESS')}
                placeholder={t('YARD_ADDRESS')}
                searchType={{
                  includeCargomaticAddresses: 1,
                  includeGoogleAddresses: 0,
                }}
                error={
                  errors.address || errors.marketId
                    ? { message: t('INVALID_ADDRESS') }
                    : null
                }
                value={yardFormData.address}
                readOnly={!!isReadOnly && !isNewYard}
                onChange={(value) =>
                  handleYardFormChange({
                    address: value,
                    marketId: null,
                  })
                }
                onSelect={(value) => {
                  handleYardFormChange({
                    address: value.address,
                    marketId: value.market?.market_id,
                  });
                }}
              />
            </div>
          </div>
          <hr className="my-4" />

          <div className="grid grid-cols-2 gap-6">
            <div className="col-span-1">
              <CargomaticInput
                name="yardContactName"
                type="text"
                label={t('CONTACT_NAME')}
                placeholder={t('CONTACT_NAME')}
                error={errors.contactName}
                value={yardFormData.contactName}
                readOnly={!!isReadOnly && !isNewYard}
                onChange={(value) =>
                  handleYardFormChange({ contactName: value })
                }
              />
            </div>

            <div className="col-span-1">
              <CargomaticInput
                name="yardContactPhone"
                type="tel"
                label={t('CONTACT_PHONE')}
                placeholder={t('CONTACT_PHONE')}
                error={errors.contactNumber}
                value={yardFormData.contactNumber}
                readOnly={!!isReadOnly && !isNewYard}
                onChange={(value) =>
                  handleYardFormChange({ contactNumber: value })
                }
              />
            </div>
          </div>

          <div className="flex justify-end mt-10">
            {isReadOnly && !isNewYard ? (
              <button
                data-testid="yardDetailsEdit"
                className="button button-green"
                onClick={handleEdit}
              >
                {t('EDIT')}
              </button>
            ) : (
              <>
                <button
                  name="yardDetailsSave"
                  className="button button-green"
                  onClick={handleSave}
                  style={{ marginRight: 15 }}
                >
                  {t('SAVE')}
                </button>

                <button
                  data-testid="yardDetailsCancel"
                  className="button button-secondary"
                  onClick={handleCancel}
                >
                  {t('CANCEL')}
                </button>
              </>
            )}
          </div>
        </Card>
      )}
    </>
  );
}

YardDetails.propTypes = {
  yard: PropTypes.shape({
    address: PropTypes.string.isRequired,
    contactName: PropTypes.string.isRequired,
    contactNumber: PropTypes.string.isRequired,
    createdAt: PropTypes.string,
    status: PropTypes.string,
    vendor: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    nameExternal: PropTypes.string.isRequired,
    nameInternal: PropTypes.string.isRequired,
    spots: PropTypes.number.isRequired,
  }),
};

YardDetails.defaultProps = {
  yard: {},
};

export default withYardLayout('yard', YardDetails);
